import { Suspense } from "react";
import { Routes, Route } from "react-router-dom";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { RootLayout } from "@/components/layout/root-layout";
import { LoadingBlock } from "@/components/atoms";
import { AuthGuard } from "@/components/auth/auth-guard";
import { AdminGuard } from "@/components/auth/admin-guard";

// Public, code-split routes.
const Home = lazyWithRetry(() => import("./home"));
const Shop = lazyWithRetry(() => import("./shop"));
const Category = lazyWithRetry(() => import("./category"));
const ProductDetail = lazyWithRetry(() => import("./product-detail"));
const Cart = lazyWithRetry(() => import("./cart"));
const Checkout = lazyWithRetry(() => import("./checkout"));
const OrderConfirmed = lazyWithRetry(() => import("./order-confirmed"));
const About = lazyWithRetry(() => import("./about"));
const Cbd101 = lazyWithRetry(() => import("./cbd-101"));
const Faq = lazyWithRetry(() => import("./faq"));
const Contact = lazyWithRetry(() => import("./contact"));
const Wholesale = lazyWithRetry(() => import("./wholesale"));
const SearchPage = lazyWithRetry(() => import("./search"));
const NotFound = lazyWithRetry(() => import("./not-found"));

// Legal (named exports → wrap as default-less lazies via dynamic import map).
const Legal = {
  Privacy: lazyWithRetry(() =>
    import("./legal/legal-page").then((m) => ({ default: m.Privacy })),
  ),
  Terms: lazyWithRetry(() =>
    import("./legal/legal-page").then((m) => ({ default: m.Terms })),
  ),
  Shipping: lazyWithRetry(() =>
    import("./legal/legal-page").then((m) => ({ default: m.Shipping })),
  ),
  Returns: lazyWithRetry(() =>
    import("./legal/legal-page").then((m) => ({ default: m.Returns })),
  ),
  Disclaimer: lazyWithRetry(() =>
    import("./legal/legal-page").then((m) => ({ default: m.Disclaimer })),
  ),
};

// Account.
const SignIn = lazyWithRetry(() =>
  import("./account/auth-screen").then((m) => ({ default: m.SignIn })),
);
const SignUp = lazyWithRetry(() =>
  import("./account/auth-screen").then((m) => ({ default: m.SignUp })),
);
const PasswordReset = lazyWithRetry(() =>
  import("./account/auth-screen").then((m) => ({ default: m.PasswordReset })),
);
const AccountLayout = lazyWithRetry(() => import("./account/account-layout"));
const AccountDashboard = lazyWithRetry(() =>
  import("./account/account-pages").then((m) => ({
    default: m.AccountDashboard,
  })),
);
const AccountOrders = lazyWithRetry(() =>
  import("./account/account-pages").then((m) => ({ default: m.Orders })),
);
const AccountOrderDetail = lazyWithRetry(() =>
  import("./account/account-pages").then((m) => ({ default: m.OrderDetail })),
);
const AccountSubscriptions = lazyWithRetry(() =>
  import("./account/account-pages").then((m) => ({ default: m.Subscriptions })),
);
const AccountAddresses = lazyWithRetry(() =>
  import("./account/account-pages").then((m) => ({ default: m.Addresses })),
);
const AccountProfile = lazyWithRetry(() =>
  import("./account/account-pages").then((m) => ({ default: m.Profile })),
);

// Admin.
const AdminLayout = lazyWithRetry(() => import("./admin/admin-layout"));
const AdminDashboard = lazyWithRetry(() =>
  import("./admin/admin-pages").then((m) => ({ default: m.AdminDashboard })),
);
const AdminProducts = lazyWithRetry(() =>
  import("./admin/admin-pages").then((m) => ({ default: m.AdminProducts })),
);
const AdminProductEdit = lazyWithRetry(() =>
  import("./admin/admin-pages").then((m) => ({ default: m.AdminProductEdit })),
);
const AdminCategories = lazyWithRetry(() =>
  import("./admin/admin-pages").then((m) => ({ default: m.AdminCategories })),
);
const AdminOrders = lazyWithRetry(() =>
  import("./admin/admin-pages").then((m) => ({ default: m.AdminOrders })),
);
const AdminOrderDetail = lazyWithRetry(() =>
  import("./admin/admin-pages").then((m) => ({ default: m.AdminOrderDetail })),
);
const AdminPages = lazyWithRetry(() =>
  import("./admin/admin-pages").then((m) => ({ default: m.AdminPages })),
);
const AdminTestimonials = lazyWithRetry(() =>
  import("./admin/admin-pages").then((m) => ({ default: m.AdminTestimonials })),
);
const AdminFaqs = lazyWithRetry(() =>
  import("./admin/admin-pages").then((m) => ({ default: m.AdminFaqs })),
);
const AdminPressArticles = lazyWithRetry(() =>
  import("./admin/admin-pages").then((m) => ({
    default: m.AdminPressArticles,
  })),
);
const AdminDiscountCodes = lazyWithRetry(() =>
  import("./admin/admin-pages").then((m) => ({
    default: m.AdminDiscountCodes,
  })),
);
const AdminSettings = lazyWithRetry(() =>
  import("./admin/admin-pages").then((m) => ({ default: m.AdminSettings })),
);
const AdminReviews = lazyWithRetry(() =>
  import("./admin/admin-pages").then((m) => ({ default: m.AdminReviews })),
);
const AdminUsers = lazyWithRetry(() =>
  import("./admin/admin-pages").then((m) => ({ default: m.AdminUsers })),
);
const AdminInbox = lazyWithRetry(() =>
  import("./admin/admin-pages").then((m) => ({ default: m.AdminInbox })),
);

export function AppRoutes() {
  return (
    <Suspense fallback={<LoadingBlock />}>
      <Routes>
        <Route element={<RootLayout />}>
          {/* Public */}
          <Route path="/" element={<Home />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/shop/:slug" element={<Category />} />
          <Route path="/products/:slug" element={<ProductDetail />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route
            path="/order/confirmed/:orderId"
            element={<OrderConfirmed />}
          />
          <Route path="/about" element={<About />} />
          <Route path="/cbd-101" element={<Cbd101 />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/contact" element={<Contact />} />
          {/* DRB-69 — the wholesale back end had no public entry point; this is it. */}
          <Route path="/wholesale" element={<Wholesale />} />
          <Route path="/search" element={<SearchPage />} />

          {/* Legal */}
          <Route path="/legal/privacy" element={<Legal.Privacy />} />
          <Route path="/legal/terms" element={<Legal.Terms />} />
          <Route path="/legal/shipping" element={<Legal.Shipping />} />
          <Route path="/legal/returns" element={<Legal.Returns />} />
          <Route path="/legal/disclaimer" element={<Legal.Disclaimer />} />

          {/* Auth (public) */}
          <Route path="/account/sign-in" element={<SignIn />} />
          <Route path="/account/sign-up" element={<SignUp />} />
          <Route path="/account/password-reset" element={<PasswordReset />} />

          {/* Account (guarded) */}
          <Route element={<AuthGuard />}>
            <Route element={<AccountLayout />}>
              <Route path="/account" element={<AccountDashboard />} />
              <Route path="/account/orders" element={<AccountOrders />} />
              <Route
                path="/account/orders/:orderId"
                element={<AccountOrderDetail />}
              />
              <Route
                path="/account/subscriptions"
                element={<AccountSubscriptions />}
              />
              <Route path="/account/addresses" element={<AccountAddresses />} />
              <Route path="/account/profile" element={<AccountProfile />} />
            </Route>
          </Route>

          <Route path="*" element={<NotFound />} />
        </Route>

        {/* Admin (guarded, no storefront chrome) */}
        <Route element={<AdminGuard />}>
          <Route element={<RootLayout />}>
            <Route element={<AdminLayout />}>
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/products" element={<AdminProducts />} />
              <Route
                path="/admin/products/:id"
                element={<AdminProductEdit />}
              />
              <Route path="/admin/categories" element={<AdminCategories />} />
              <Route path="/admin/orders" element={<AdminOrders />} />
              <Route path="/admin/orders/:id" element={<AdminOrderDetail />} />
              <Route path="/admin/reviews" element={<AdminReviews />} />
              <Route path="/admin/discounts" element={<AdminDiscountCodes />} />
              <Route path="/admin/inbox" element={<AdminInbox />} />
              {/* DRB-70 — People. Commandment 2 requires it and it had never been built. */}
              <Route path="/admin/users" element={<AdminUsers />} />
              <Route path="/admin/content/pages" element={<AdminPages />} />
              <Route
                path="/admin/content/testimonials"
                element={<AdminTestimonials />}
              />
              <Route path="/admin/content/faqs" element={<AdminFaqs />} />
              <Route
                path="/admin/content/press"
                element={<AdminPressArticles />}
              />
              <Route path="/admin/settings" element={<AdminSettings />} />
            </Route>
          </Route>
        </Route>
      </Routes>
    </Suspense>
  );
}
